import { useState } from "react";
import {
    Button,
    Input,
    message,
    Space,
    Typography,
    Modal,
    Spin,
    Divider,
    Flex,
    Card,
} from "antd";
import { PDFDocument } from "pdf-lib";
import GeneralUploader from "./Uploader";
import { downloadBlob } from "./utils";

const { Title, Text } = Typography;

export default function SplitPDF() {
    const [file, setFile] = useState(null);
    const [range, setRange] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [splitting, setSplitting] = useState(false);
    const [splitFiles, setSplitFiles] = useState([]);
    const [showUploader, setShowUploader] = useState(true);

    const resetUploaderState = () => {
        setModalVisible(false);
        setShowUploader(true);
        setFile(null);
        setRange("");
    };

    const handleUploadChange = (info) => {
        const uploaded = Array.isArray(info) ? info[0] : info?.file ?? info;
        if (!uploaded || uploaded.status === "uploading") return;

        const actualFile = uploaded.originFileObj ?? uploaded;
        if (typeof actualFile?.arrayBuffer !== "function") {
            message.error("Please upload a valid PDF file");
            setFile(null);
            return;
        }

        setFile(actualFile);
        setShowUploader(false);
        setModalVisible(true);
    };

    const parseRanges = (rangeStr, total) => {
        if (!rangeStr) return [Array.from({ length: total }, (_, i) => i)];

        const groups = rangeStr.split(";").map((g) => g.trim());
        const allGroups = [];

        for (const group of groups) {
            if (!group) continue;
            const indices = [];
            const parts = group.split(",").map((p) => p.trim());

            for (const part of parts) {
                if (!part) continue;
                if (part.includes("-")) {
                    const [sRaw, eRaw] = part.split("-").map((p) => p.trim());
                    const s = Number(sRaw);
                    const e = Number(eRaw);
                    if (isNaN(s) || isNaN(e) || s < 1 || e > total || s > e)
                        throw new Error("Invalid range");
                    for (let i = s; i <= e; i++) indices.push(i - 1);
                } else {
                    const n = Number(part);
                    if (isNaN(n) || n < 1 || n > total)
                        throw new Error("Invalid page");
                    indices.push(n - 1);
                }
            }

            const uniqueSorted = [...new Set(indices)].sort((a, b) => a - b);
            if (uniqueSorted.length > 0) allGroups.push(uniqueSorted);
        }

        if (allGroups.length === 0) throw new Error("No valid pages specified");
        return allGroups;
    };

    const handleSplit = async () => {
        if (!file) {
            message.error("Please upload a PDF file");
            return;
        }

        const key = "split";
        message.loading({ content: "Splitting PDF...", key, duration: 0 });
        setSplitting(true);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const totalPages = pdfDoc.getPageCount();
            const groups = parseRanges(range, totalPages);

            const outputs = [];
            for (let i = 0; i < groups.length; i++) {
                const newDoc = await PDFDocument.create();
                const copiedPages = await newDoc.copyPages(pdfDoc, groups[i]);
                copiedPages.forEach((p) => newDoc.addPage(p));
                const bytes = await newDoc.save();
                const filename = `${file.name.replace(/\.pdf$/i, "")}_split_${
                    i + 1
                }.pdf`;
                outputs.push({ bytes, filename });
            }

            setSplitFiles(outputs);
            setModalVisible(false);
            message.success({ content: "PDF split successfully!", key });
        } catch (error) {
            message.error({
                content: `Error: ${
                    error?.message ??
                    "Failed to split PDF. Check ranges and try again."
                }`,
                key,
            });
        } finally {
            setSplitting(false);
        }
    };

    const handleDownloadPart = (bytes, filename) => {
        if (!bytes) return;
        downloadBlob(bytes, filename);
    };

    const handleStartOver = () => {
        setFile(null);
        setRange("");
        setModalVisible(false);
        setSplitting(false);
        setSplitFiles([]);
        setShowUploader(true);
    };

    const handleCancel = () => {
        if (splitting) return;
        resetUploaderState();
    };

    return (
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <Flex
                vertical
                gap={40}
                align="center"
                style={{
                    padding: "40px 20px",
                }}
            >
                <Flex vertical align="center">
                    <Title level={1}>Split PDF File</Title>
                    <Text>
                        Choose specific page ranges and split into one or
                        multiple PDFs
                    </Text>
                </Flex>

                {showUploader && splitFiles.length === 0 && (
                    <GeneralUploader
                        type="pdf"
                        accept=".pdf"
                        multiple={false}
                        showUploadList
                        beforeUpload={() => false}
                        onChange={handleUploadChange}
                    />
                )}

                {splitFiles.length > 0 && (
                    <Card
                        size="default"
                        style={{ padding: 20, textAlign: "center" }}
                    >
                        <Title level={3} style={{ margin: 0 }}>
                            Your files are ready
                        </Title>

                        <Flex vertical gap={12} style={{ marginTop: 20 }}>
                            {splitFiles.map((f, idx) => (
                                <Button
                                    key={f.filename}
                                    type="primary"
                                    size="large"
                                    onClick={() =>
                                        handleDownloadPart(f.bytes, f.filename)
                                    }
                                    style={{ minWidth: 220 }}
                                >
                                    Download{" "}
                                    {splitFiles.length > 1
                                        ? `Part ${idx + 1}`
                                        : ""}
                                </Button>
                            ))}
                        </Flex>

                        <div style={{ marginTop: 14 }}>
                            <Button
                                size="large"
                                onClick={handleStartOver}
                                style={{ minWidth: 220 }}
                            >
                                Start Over
                            </Button>
                        </div>
                    </Card>
                )}
            </Flex>

            <Modal
                title="Split PDF enter page ranges"
                centered
                open={modalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button
                        key="cancel"
                        onClick={handleCancel}
                        disabled={splitting}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="split"
                        type="primary"
                        onClick={handleSplit}
                        loading={splitting}
                    >
                        Split
                    </Button>,
                ]}
                destroyOnHidden
                maskClosable={!splitting}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Input
                        placeholder="e.g. 1-5,8,11-13 or 1-3;4-6 for multiple files (leave blank for all)"
                        value={range}
                        onChange={(e) => setRange(e.target.value)}
                        size="default"
                        onPressEnter={handleSplit}
                        disabled={splitting}
                    />

                    {splitting && (
                        <Flex vertical align="center">
                            <Spin />
                            <div style={{ marginTop: 8 }}>
                                Splitting PDF please wait...
                            </div>
                        </Flex>
                    )}

                    <Divider style={{ margin: 6 }} />

                    <Text>
                        Examples: <br />
                        <code>1-3</code> → pages 1 to 3 as one file. <br />
                        <code>1-3;4-6</code> → two files (pages 1–3 and 4–6).{" "}
                        <br />
                        <code>1,3,5</code> → pages 1, 3 and 5 in one file.
                    </Text>
                </Space>
            </Modal>
        </div>
    );
}
